import { AuthContext } from "@/hooks/auth/AuthContext";
import { clearBuildingSetupDraft } from "../buildingSetupDraft";
import { useBuildingSetupDraft } from "../useBuildingSetupDraft";
import { useCallback, useContext, useEffect, useRef, useState, type MutableRefObject } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Dialog } from "@/components/ui/dialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { BuildingCreationDialogContent } from "@/domains/buildings/components/form/BuildingCreationDialogContent";
import {
  createBuildingSchema,
  type BuildingCreationValues,
} from "@/domains/buildings/components/form/buildingCreationSchema";
import { useAddBillingBuilding, useBillingPlans } from "../hooks";
import { billingErrorMessage } from "../messages";
import type { BillingContext, BillingOverview, BuildingPlanTier } from "../types";
import type { Translations } from "@/lib/i18n/types";
import {
  paidBuildingCreationBlock,
  paidBuildingSetupNeeded,
  quoteForBuildingPlan,
} from "../buildingPlanCreation";

interface AddBuildingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  quote: BillingOverview["add_building_quote"];
  currency: string;
  context: BillingContext;
  quoteRefreshing?: boolean;
}

type CreationAttempt = { signature: string; id: string };

function creationRequestId(
  attemptRef: MutableRefObject<CreationAttempt | null>,
  signature: string
) {
  if (attemptRef.current?.signature !== signature)
    attemptRef.current = { signature, id: crypto.randomUUID() };
  return attemptRef.current.id;
}

function reportBuildingCreated(pending: boolean, t: Translations) {
  if (pending) toast.info(t.billing.plans.activationPending);
  else toast.success(t.billing.addDialog.added);
}

function canSubmitCreation(
  reason: string | undefined,
  planAvailable: boolean,
  unavailable: boolean,
  loading: boolean,
  pending: boolean
) {
  return !reason && planAvailable && !unavailable && !loading && !pending;
}

function creationQuoteIsLoading(
  catalogLoading: boolean,
  catalogFetching: boolean,
  overviewRefreshing?: boolean
) {
  return catalogLoading || catalogFetching || Boolean(overviewRefreshing);
}

function useCreationReset(
  open: boolean,
  pending: boolean,
  form: UseFormReturn<BuildingCreationValues>,
  attemptRef: MutableRefObject<CreationAttempt | null>,
  setTier: (tier: BuildingPlanTier) => void
) {
  const previousOpenRef = useRef(open);
  const reset = useCallback(() => {
    form.reset();
    attemptRef.current = null;
    setTier("free");
  }, [form, attemptRef, setTier]);
  useEffect(() => {
    const closing = previousOpenRef.current && !open;
    previousOpenRef.current = open;
    if (!closing || pending) return;
    reset();
  }, [open, pending, reset]);
  return reset;
}

function AddBuildingContent(props: AddBuildingDialogProps) {
  const { t } = useLanguage();
  const mutation = useAddBillingBuilding();
  const creationRequestRef = useRef<CreationAttempt | null>(null);
  const plans = useBillingPlans(props.open);
  const [tier, setTier] = useState<BuildingPlanTier>("free");
  const selectedPlan = plans.data?.find((plan) => plan.code === tier);
  const isLoading = creationQuoteIsLoading(
    plans.isLoading,
    plans.isFetching,
    props.quoteRefreshing
  );
  const reason = !props.context.is_org_owner
    ? t.billing.restrictions.ownerOnly
    : props.context.org_id !== props.orgId
      ? t.billing.consent.quoteUnavailable
      : paidBuildingCreationBlock(props.context, tier, t);
  const unavailable = plans.isError || (!isLoading && !selectedPlan);
  const canSubmit = canSubmitCreation(
    reason,
    Boolean(selectedPlan),
    unavailable,
    isLoading,
    mutation.isPending
  );
  const form = useForm<BuildingCreationValues>({
    resolver: zodResolver(createBuildingSchema(t)),
    defaultValues: { name: "", address: "", registry_code: "" },
  });
  const setupDraft = useBuildingSetupDraft(props.orgId, form, tier, setTier);
  const reset = useCreationReset(props.open, mutation.isPending, form, creationRequestRef, setTier);
  const submit = async (values: BuildingCreationValues) => {
    if (!canSubmit) return;
    const fields = {
      orgId: props.orgId,
      name: values.name,
      address: values.address,
      registryCode: values.registry_code || null,
      tier,
    };
    try {
      const result = await mutation.mutateAsync({
        ...fields,
        creationRequestId: creationRequestId(creationRequestRef, JSON.stringify(fields)),
      });
      reportBuildingCreated(result.activation_pending || result.status === "pending", t);
      reset();
      setupDraft.clearDraft();
      props.onOpenChange(false);
    } catch (error) {
      toast.error(billingErrorMessage(error, t, t.billing.addDialog.addFailed));
    }
  };
  return (
    <BuildingCreationDialogContent
      form={form}
      onSubmit={submit}
      onCancel={() => {
        setupDraft.clearDraft();
        props.onOpenChange(false);
      }}
      disabled={mutation.isPending}
      submitting={mutation.isPending}
      submitDisabled={!canSubmit}
      selection={{
        plans: plans.data,
        tier,
        setTier,
        quote: isLoading ? undefined : quoteForBuildingPlan(props.quote, selectedPlan),
        currency: props.currency,
        unavailable,
        isLoading,
        reason,
        needsBillingSetup: paidBuildingSetupNeeded(props.context, tier),
        onSetup: setupDraft.startSetup,
        retry: () => void plans.refetch(),
      }}
    />
  );
}

export function AddBuildingDialog(props: AddBuildingDialogProps) {
  const userId = useContext(AuthContext)?.userId;
  const changeOpen = (open: boolean) => {
    if (!open) clearBuildingSetupDraft(userId, props.orgId);
    props.onOpenChange(open);
  };
  return (
    <Dialog open={props.open} onOpenChange={changeOpen}>
      <AddBuildingContent key={props.orgId} {...props} />
    </Dialog>
  );
}
